import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ReferenceDot,
} from 'recharts';
import {
  niceScale, tickFormatter, dateTicks, fmtDay, fmtSpan, fmtInt, fmtRate, nullRuns, WEEK_TICK_STEPS, DAY_TICK_STEPS,
} from '../../lib/slotStatsView';
import {
  AxisFrame, TooltipBox, DataEndBar, StackSegment, AXIS_TICK, yAxisWidth, actionColor,
} from './ChartKit';
import { maxDateTicks, notRecordedAreas } from './GrowthCharts';
import useIsPhone from '../../lib/useIsPhone';
import { cn } from '@/lib/utils';

/*
 * Round 14b: the actions chart, its metric tabs and the totals table.
 *
 * "All actions" is a STACKED bar per day (per week at 90 days), one segment per action type in one
 * fixed order - METRICS order, the first type at the bottom - the same order as the legend, the
 * tooltip and the totals table (owner, 2026-09-29: "Stacked, all 8 types", over the review's
 * one-colour total). The small types (welcome DMs, posts) are thin slivers under the big ones, so:
 *   - the legend is interactive: a click hides / shows a type and the y axis rescales to what is
 *     left (at least one type stays shown; "Show all" resets);
 *   - hovering a legend item or a segment highlights that type (a lift toward white) and dims the
 *     others (a sliver thinner than 3px is drawn 3px tall while highlighted);
 *   - each day's bar is one continuous column of colours, no gap between its segments (owner,
 *     2026-09-29), in a vivid palette (stats.css --viz-act-N);
 *   - one tooltip per day lists all the types with the day's total, hidden types marked.
 * The metric tabs above the plot carry each type's range total; a type's tab charts it alone, in its
 * own colour, with its average line.
 */

/** A colour key that mirrors a bar mark (rect) - identity never rides on coloured text. */
function Swatch({ color, hollow, className }) {
  return (
    <span className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]', className)} aria-hidden
      style={hollow ? { border: `1.5px solid ${color}`, background: 'transparent' } : { background: color }} />
  );
}

/** The metric tabs: every recorded type's total over the range; the pressed one is charted. */
export function MetricTabs({ summary, selected, onSelect, testid }) {
  return (
    <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5 xl:grid-cols-9" role="group" aria-label="Chart this action type"
      data-testid={testid}>
      {summary.map((s) => {
        const on = s.key === selected;
        return (
          <button key={s.key} type="button" onClick={() => onSelect(s.key)} aria-pressed={on}
            className={cn(
              'tap flex min-w-0 flex-col items-start rounded-xl border px-2 py-2 text-left transition-colors sm:px-2.5',
              on ? 'border-primary/60 bg-primary/10' : 'border-white/5 bg-white/[.02] hover:border-white/15 hover:bg-white/5',
            )}
            data-testid={testid && `${testid}-${s.key}`}>
            <span className={cn('flex w-full min-w-0 items-center gap-1 text-[11px] font-medium sm:gap-1.5', on ? 'text-foreground' : 'text-muted-foreground')}>
              {s.key !== 'all' && <Swatch color={actionColor(s.key)} className="h-2 w-2 rounded-[2px]" />}
              <span className="truncate">{s.label}</span>
            </span>
            <span className="mt-0.5 text-base font-semibold leading-tight text-foreground">{fmtInt(s.total)}</span>
          </button>
        );
      })}
    </div>
  );
}

const bucketTitle = (d, weekly, label) => {
  const date = d ? d.date : label;
  if (!date) return null;
  return weekly && d
    ? `${fmtSpan(d.from, d.to)}${d.recorded < 7 ? ` · ${d.recorded} of 7 days recorded` : ''}`
    : fmtDay(date, 'long');
};

const runningFooter = (weekly, avgNote = true) => (weekly
  ? 'Includes today so far: the day is not over.'
  : `So far today: the day is not over${avgNote ? ', so it is left out of the average' : ''}.`);

/** One type alone: its value that day (or week). */
function ActionsTip({ active, payload, label, metric, weekly, color }) {
  if (!active) return null;
  const d = payload && payload[0] ? payload[0].payload : null;
  const title = bucketTitle(d, weekly, label);
  if (!title) return null;
  if (!d || d.value === null) {
    return <TooltipBox title={title} rows={[{ key: 'nr', value: 'Not recorded', label: `the slot kept no count that ${weekly ? 'week' : 'day'}` }]} />;
  }
  const rows = [{ key: 'v', color, value: fmtInt(d.value), label: `${metric.label.toLowerCase()}${d.running ? ' so far' : ''}` }];
  return <TooltipBox title={title} rows={rows} footer={d.running ? runningFooter(weekly) : null} />;
}

/**
 * The stacked chart's tooltip: the day's total, then EVERY type in the stack's order with its value
 * (a hidden type marked "hidden", a type with no count that day "not recorded"); the highlighted
 * type's row lifts. r16n: `colorOf`, `totalNoun` and `extraFooter(d)` let the Story shares card use it for its two audiences
 * (defaults: the action types, exactly as before).
 */
export function StackTip({
  active, payload, label, series, hidden, hi, weekly, colorOf = actionColor, totalNoun = 'actions', extraFooter,
}) {
  if (!active) return null;
  const d = payload && payload[0] ? payload[0].payload : null;
  const title = bucketTitle(d, weekly, label);
  if (!title) return null;
  if (!d || d.total === null) {
    return <TooltipBox title={title} rows={[{ key: 'nr', value: 'Not recorded', label: `the slot kept no count that ${weekly ? 'week' : 'day'}` }]} />;
  }
  const rows = [{ key: 'total', value: fmtInt(d.total), label: d.running ? `${totalNoun} in total so far` : `${totalNoun} in total` }];
  series.forEach((s) => {
    const v = d.parts[s.key];
    const off = hidden.includes(s.key);
    const rec = v !== null && v !== undefined;
    rows.push({
      key: s.key,
      color: colorOf(s.key),
      medium: true,
      value: rec ? fmtInt(v) : '-',
      label: !rec ? `${s.label} (not recorded)` : (off ? `${s.label} (hidden)` : s.label),
      muted: off || !rec,
      lit: hi === s.key,
    });
  });
  const footer = [];
  if (hidden.length) {
    footer.push(
      <p key="hidden">
        {d.shown === null
          // only hidden types were recorded then: no bar is drawn, so there is no number to give
          ? `No bar: none of the shown types was recorded that ${weekly ? 'week' : 'day'}; `
          : <>The bar shows <span className="tnum font-medium text-foreground">{fmtInt(d.shown)}</span>: </>}
        {hidden.length} type{hidden.length === 1 ? ' is' : 's are'} hidden.
      </p>,
    );
  }
  const extra = extraFooter ? extraFooter(d) : null;
  if (extra) footer.push(<p key="extra">{extra}</p>);
  if (d.running) footer.push(<p key="running">{runningFooter(weekly)}</p>);
  return <TooltipBox title={title} rows={rows} footer={footer.length ? footer : null} testid="viz-tooltip" />;
}

/** The label at the top of a vertical rule: right of it, or left of it when it would run off the plot. */
function RuleLabel({ viewBox, text, chartWidth }) {
  if (!viewBox) return null;
  const x = viewBox.x;
  const left = x + 8 + text.length * 6.2 > chartWidth - 4;
  return (
    <text x={left ? x - 6 : x + 6} y={viewBox.y + 12} textAnchor={left ? 'end' : 'start'} fill="var(--viz-ink-2)" fontSize={11}>
      {text}
    </text>
  );
}

/** "so far" over the bar of a day (or week) still in progress; a surface halo keeps it readable over a rule. */
function SoFarLabel({ viewBox }) {
  if (!viewBox) return null;
  const cx = viewBox.x + (viewBox.width || 0) / 2;
  return (
    <text x={cx} y={viewBox.y - 6} textAnchor="middle" fill="var(--viz-ink-2)" fontSize={10} fontWeight={500}
      stroke="var(--viz-surface)" strokeWidth={3} strokeLinejoin="round" paintOrder="stroke"
      className="viz-sofar" data-testid="viz-sofar">
      so far
    </text>
  );
}

/** The width "so far" needs (10px text): narrower bar slots (30 days on a phone) leave it to the tooltip and the note. */
const SO_FAR_MIN_SLOT = 28;

/**
 * The running bar's "so far" mark, when its label fits inside the bar's own slot (it would otherwise
 * run over the neighbouring bar). plotWidth: the chart width less the y axis and the margins.
 */
const soFarDot = (row, y, plotWidth, n) => (row && plotWidth / Math.max(1, n) >= SO_FAR_MIN_SLOT ? (
  <ReferenceDot key="sofar" x={row.date} y={y} r={1} fill="none" stroke="none" ifOverflow="visible" label={<SoFarLabel />} />
) : null);

const avgLine = (avg) => (
  <ReferenceLine y={avg} stroke="var(--viz-ink-2)" strokeDasharray="4 3" strokeWidth={1}
    label={{ value: `avg ${fmtRate(avg, { signed: false })}`, position: 'right', fill: 'var(--viz-ink-2)', fontSize: 11 }} />
);

const lateRules = (late, weekly, bars, width, word = 'type') => {
  const bucketOf = (date) => (bars.find((b) => b.from <= date && date <= b.to) || {}).date;
  return late.map((l) => (
    <ReferenceLine key={`late-${l.date}`} x={weekly ? bucketOf(l.date) : l.date} position="start"
      stroke="var(--viz-ink-3)" strokeWidth={1}
      label={<RuleLabel chartWidth={width} text={`+${l.labels.length} ${word}${l.labels.length === 1 ? '' : 's'} from ${fmtDay(l.date)}`} />} />
  ));
};

/**
 * One type alone. bars: actionBars() (oldest first); metric: { key, label }; avg: its average per
 * complete day (dashed, daily bars only); color: the type's colour.
 */
export function ActionsChart({ bars, metric, avg, weekly, color, height = 260, testid }) {
  const values = bars.map((b) => b.value).filter((v) => typeof v === 'number');
  if (!values.length) return null;
  const drawAvg = !weekly && avg !== null && avg !== undefined;
  const scale = niceScale(0, Math.max(...values, drawAvg ? avg : 0), { count: 5, zero: true });
  const fmt = tickFormatter(scale.step, scale.hi);
  const days = bars.map((b) => b.date);
  const runs = nullRuns(bars, (b) => b.value);
  const yTitle = `${metric.label} per ${weekly ? 'week' : 'day'}`;
  const running = bars.find((b) => b.running && b.value);
  const right = drawAvg ? 58 : 24;
  const yW = yAxisWidth(scale.ticks, fmt);
  return (
    <AxisFrame yTitle={yTitle} xTitle={weekly ? 'Week ending' : 'Date'} testid={testid}>
      {(width) => (
        <BarChart width={width} height={height} data={bars} margin={{ top: 18, right, bottom: 0, left: 0 }}
          barCategoryGap={weekly ? '28%' : '18%'} role="img"
          aria-label={`${yTitle}, ${fmtDay(bars[0].from, 'medium')} to ${fmtDay(days[days.length - 1], 'medium')}`}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          {notRecordedAreas(runs, width, days.length)}
          <XAxis dataKey="date" ticks={dateTicks(days, maxDateTicks(width), weekly ? WEEK_TICK_STEPS : DAY_TICK_STEPS)} interval={0}
            tickFormatter={(d) => fmtDay(d)} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8} />
          <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt}
            width={yW} tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={6}
            allowDataOverflow />
          <Tooltip filterNull={false} isAnimationActive={false} cursor={{ fill: 'var(--viz-hover)' }}
            content={(p) => <ActionsTip {...p} metric={metric} weekly={weekly} color={color} />} />
          <Bar dataKey="value" name={metric.label} fill={color} maxBarSize={24} isAnimationActive={false}
            shape={(p) => <DataEndBar {...p} vivid />} activeBar={(p) => <DataEndBar {...p} vivid isActive />} />
          {drawAvg && avgLine(avg)}
          {soFarDot(running, running && running.value, width - yW - right, bars.length)}
        </BarChart>
      )}
    </AxisFrame>
  );
}

/**
 * All actions, stacked. stack: actionStack() over the range's bars; series: [{ key, label }] in the
 * stack's order (bottom first); late: lateStarts(); hi / onHi: the highlighted type.
 * r16n: `colorOf` (each series' colour), `segKeys` (the stack's keys, bottom up, for StackSegment), `yTitle`, `noun` (the aria
 * label's "by action type"), `lateWord` (the late-start rule's "+1 type") and `tipProps` (StackTip's colorOf / totalNoun /
 * extraFooter) let the Story shares card stack its two audiences the same seamless way. Defaults: the "All actions" chart,
 * exactly as before.
 */
export function StackedActionsChart({
  stack, series, weekly, late = [], hi, onHi, height = 280, testid,
  colorOf = actionColor, segKeys, yTitle: yTitleIn, noun = 'action type', lateWord = 'type', tipProps,
}) {
  const { rows, visible, hidden } = stack;
  if (!rows.some((r) => r.total !== null)) return null;
  const drawAvg = !weekly && stack.avg !== null;
  const scale = niceScale(0, Math.max(stack.max, drawAvg ? stack.avg : 0), { count: 5, zero: true });
  const fmt = tickFormatter(scale.step, scale.hi);
  const days = rows.map((r) => r.date);
  const runs = nullRuns(rows, (r) => r.shown);
  const yTitle = yTitleIn || `Actions per ${weekly ? 'week' : 'day'}`;
  const drawn = series.filter((s) => visible.includes(s.key));
  const running = rows.find((r) => r.running && r.shown);
  const right = drawAvg ? 58 : 24;
  const yW = yAxisWidth(scale.ticks, fmt);
  return (
    <AxisFrame yTitle={yTitle} xTitle={weekly ? 'Week ending' : 'Date'} testid={testid}>
      {(width) => (
        // keyed by the stack's types: a range that records a different set of types remounts the chart,
        // so its bars register (and stack) in METRICS order again
        <BarChart key={series.map((s) => s.key).join(',')} width={width} height={height} data={rows}
          margin={{ top: 18, right, bottom: 0, left: 0 }}
          barCategoryGap={weekly ? '28%' : '18%'} role="img"
          onMouseLeave={() => onHi && onHi(null)}
          aria-label={`${yTitle} by ${noun} (${drawn.map((s) => s.label).join(', ')}${hidden.length ? `; ${hidden.length} hidden` : ''}), `
            + `${fmtDay(rows[0].from, 'medium')} to ${fmtDay(days[days.length - 1], 'medium')}`}>
          <CartesianGrid vertical={false} stroke="var(--viz-grid)" />
          {notRecordedAreas(runs, width, days.length)}
          <XAxis dataKey="date" ticks={dateTicks(days, maxDateTicks(width), weekly ? WEEK_TICK_STEPS : DAY_TICK_STEPS)} interval={0}
            tickFormatter={(d) => fmtDay(d)} tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: 'var(--viz-axis)' }} tickMargin={8} />
          <YAxis type="number" domain={[scale.lo, scale.hi]} ticks={scale.ticks} tickFormatter={fmt}
            width={yW} tick={AXIS_TICK} tickLine={false} axisLine={false} tickMargin={6}
            allowDataOverflow />
          <Tooltip filterNull={false} isAnimationActive={false} cursor={{ fill: 'var(--viz-hover)' }}
            content={(p) => <StackTip {...p} series={series} hidden={hidden} hi={hi} weekly={weekly} {...(tipProps || {})} />} />
          {/* EVERY type keeps its <Bar> (a hidden one's values are null, so it draws nothing): Recharts
              stacks in the order the bars REGISTER, and a bar mounted again after a hide registers
              last - "show" would otherwise put the type on top of the stack, out of the fixed order */}
          {series.map((s) => {
            const color = colorOf(s.key);
            const kp = segKeys ? { keys: segKeys } : {};
            return (
              <Bar key={s.key} dataKey={(r) => r.seg[s.key]} name={s.label} stackId="actions" fill={color} maxBarSize={24}
                isAnimationActive={false}
                shape={(p) => <StackSegment {...p} {...kp} typeKey={s.key} color={color} hi={hi} onHi={onHi} />}
                activeBar={(p) => <StackSegment {...p} {...kp} typeKey={s.key} color={color} hi={hi} onHi={onHi} isActive />} />
            );
          })}
          {drawAvg && avgLine(stack.avg)}
          {lateRules(late, weekly, rows, width, lateWord)}
          {soFarDot(running, running && running.shown, width - yW - right, rows.length)}
        </BarChart>
      )}
    </AxisFrame>
  );
}

/**
 * The stacked chart's legend, in the stack's order: a click hides / shows a type (the last shown
 * one cannot be hidden), hover or focus highlights it in the plot, "Show all" brings every type back.
 * r16m: `colorOf` / `noun` / `label` let the follow-back page use the same legend for its source types
 * (defaults: the action types, exactly as before).
 */
export function ActionLegend({
  series, hidden, hi, onToggle, onHi, onShowAll, testid,
  colorOf = actionColor, noun = 'types', label = 'Action types: click to hide or show',
}) {
  const shown = series.filter((s) => !hidden.includes(s.key)).length;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={testid}>
      <ul className="-ml-2 flex flex-wrap items-center gap-x-0.5 gap-y-0.5" aria-label={label}>
        {series.map((s) => {
          const off = hidden.includes(s.key);
          const last = !off && shown === 1;
          const color = colorOf(s.key);
          return (
            <li key={s.key}>
              <button type="button" aria-pressed={!off} aria-disabled={last ? true : undefined}
                title={last ? 'At least one type stays shown' : `${off ? 'Show' : 'Hide'} ${s.label}`}
                onClick={() => { if (!last) onToggle(s.key); }}
                onMouseEnter={() => onHi(off ? null : s.key)} onMouseLeave={() => onHi(null)}
                onFocus={() => onHi(off ? null : s.key)} onBlur={() => onHi(null)}
                className={cn(
                  'tap inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
                  off ? 'text-muted-foreground/70 hover:bg-white/5' : 'text-muted-foreground hover:bg-white/5 hover:text-foreground',
                  hi === s.key && 'bg-white/[.07] text-foreground',
                  last && 'cursor-default',
                )}
                data-testid={testid && `${testid}-${s.key}`}>
                <Swatch color={color} hollow={off} />
                <span className={cn(off && 'line-through decoration-1')}>{s.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {hidden.length > 0 && (
        <span className="inline-flex items-center gap-2 text-xs text-muted-foreground" data-testid={testid && `${testid}-hidden`}>
          <span>{shown} of {series.length} {noun} shown</span>
          <button type="button" onClick={onShowAll}
            className="tap inline-flex h-7 items-center rounded-full border border-white/10 px-2.5 font-medium text-foreground hover:bg-white/5"
            data-testid={testid && `${testid}-showall`}>
            Show all
          </button>
        </span>
      )}
    </div>
  );
}

/**
 * The stacked chart with its legend: owns the highlighted type (hover leaves clear it after a beat,
 * so moving from one segment, or legend item, to the next does not flicker). `hidden` lives with
 * the page, so it survives a range change or a trip through a type's tab.
 * r16n: `chartProps` go to StackedActionsChart (colorOf, segKeys, yTitle, noun, tipProps, height) and `legendProps` to ActionLegend
 * (colorOf, noun, label, testid); defaults: the "All actions" chart and legend, exactly as before.
 */
export function ActionsStack({
  stack, series, weekly, late, onToggle, onShowAll, testid, chartProps, legendProps,
}) {
  const [hi, setHi] = useState(null);
  const timer = useRef(null);
  const onHi = useCallback((k) => {
    clearTimeout(timer.current);
    if (k) setHi(k);
    else timer.current = setTimeout(() => setHi(null), 80);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const lit = hi && stack.visible.includes(hi) ? hi : null;
  return (
    <div data-testid={testid && `${testid}-stack`} data-hi={lit || undefined}>
      <StackedActionsChart stack={stack} series={series} weekly={weekly} late={late} hi={lit} onHi={onHi} testid={testid}
        {...(chartProps || {})} />
      {series.length > 1 && (
        // one recorded type is a single series: its tab and the y title name it, a legend would only repeat them
        <div className="mt-2 pl-5">
          <ActionLegend series={series} hidden={stack.hidden} hi={lit} onToggle={onToggle} onHi={onHi} onShowAll={onShowAll}
            testid="chart-actions-legend" {...(legendProps || {})} />
        </div>
      )}
    </div>
  );
}

const WINDOWS = [
  { key: 'd7', label: '7 days', size: 7 },
  { key: 'd30', label: '30 days', size: 30 },
  { key: 'd90', label: '90 days', size: 90 },
];

/** "Welcome DMs, Story views and Posts" */
const listText = (labels) => (labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`);

/** The footnote for windows longer than a type's recorded history, grouped by day count. */
function shortNote(totals) {
  const groups = {};
  totals.filter((t) => t.present && t.days < 90).forEach((t) => { (groups[t.days] = groups[t.days] || []).push(t.label); });
  const parts = Object.keys(groups).map(Number).sort((a, b) => b - a)
    .map((n) => `${listText(groups[n])}: ${n} day${n === 1 ? '' : 's'} recorded`);
  return parts.length ? `* The window is longer than the recorded history - ${parts.join('; ')}.` : null;
}

/**
 * The totals table: the counters' day, the last 7 / 30 / 90 days (back from the counters' day) and
 * the average per complete recorded day over the selected range. A type the server does not record
 * says so. A window longer than a type's history carries a * and a footnote. On a phone it reflows
 * to one block per type instead of scrolling sideways. Rows in the stack's order, each keyed by its
 * type's colour.
 */
export function ActionTotalsTable({ totals, dayHeader, range }) {
  const isPhone = useIsPhone();
  const note = shortNote(totals);
  const cell = (t, w) => (
    <>
      {fmtInt(t[w.key])}
      {t.days < w.size && <span className="text-muted-foreground" aria-label={`only ${t.days} days recorded`}>*</span>}
    </>
  );
  const missing = (t) => (t.sent ? 'not recorded yet' : 'not recorded by this server');
  const name = (t) => (
    <span className="inline-flex items-center gap-2">
      <Swatch color={actionColor(t.key)} className={cn(!t.present && 'opacity-30')} />
      {t.label}
    </span>
  );
  return (
    <div>
      {isPhone ? (
        <ul className="divide-y divide-white/5 text-sm" data-testid="stats-totals">
          {totals.map((t) => (
            <li key={t.key} className="py-2.5" data-testid={`stats-row-${t.key}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-foreground">{name(t)}</span>
                {t.present
                  ? <span className="text-xs text-muted-foreground tnum">avg {fmtRate(t.avg, { signed: false })} a day</span>
                  : <span className="text-xs text-muted-foreground">{missing(t)}</span>}
              </div>
              {t.present && (
                <dl className="mt-1.5 grid grid-cols-4 gap-2 tnum">
                  <div><dt className="text-[11px] text-muted-foreground">{dayHeader}</dt><dd className="text-foreground">{fmtInt(t.day0)}</dd></div>
                  {WINDOWS.map((w) => (
                    <div key={w.key}><dt className="text-[11px] text-muted-foreground">{w.label}</dt><dd className="text-foreground">{cell(t, w)}</dd></div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <table className="w-full text-sm tnum" data-testid="stats-totals">
          <caption className="sr-only">Actions done per type: the counters&apos; day, the last 7, 30 and 90 days, and the average per day</caption>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-3 py-2 font-medium">Action</th>
              <th scope="col" className="px-3 py-2 text-right font-medium" data-testid="stats-counter-day">{dayHeader}</th>
              {WINDOWS.map((w) => <th key={w.key} scope="col" className="px-3 py-2 text-right font-medium">{w.label}</th>)}
              <th scope="col" className="px-3 py-2 text-right font-medium" data-testid="stats-avg-header">Avg / day ({range}d)</th>
            </tr>
          </thead>
          <tbody>
            {totals.map((t) => (
              <tr key={t.key} className="border-t border-white/5" data-testid={`stats-row-${t.key}`}>
                <th scope="row" className="px-3 py-2 text-left font-normal text-foreground">{name(t)}</th>
                {t.present ? (
                  <>
                    <td className="px-3 py-2 text-right text-foreground">{fmtInt(t.day0)}</td>
                    {WINDOWS.map((w) => <td key={w.key} className="px-3 py-2 text-right text-foreground">{cell(t, w)}</td>)}
                    <td className="px-3 py-2 text-right text-muted-foreground">{fmtRate(t.avg, { signed: false })}</td>
                  </>
                ) : (
                  <td colSpan={5} className="px-3 py-2 text-right text-muted-foreground">{missing(t)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {note && <p className="mt-2 text-[11px] text-muted-foreground" data-testid="stats-totals-short-note">{note}</p>}
    </div>
  );
}
